import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { UsableError } from 'src/services/error-parsing/error-parsing.types';
import {
  adaptSurveyListEnvelope,
  buildResultsQueryParams,
  buildSurveysListQueryParams,
} from './surveys-api-v1.adapters';
import {
  getSurveysApiV1BaseUrl,
  surveysApiV1Envelope,
  SurveysApiV1RequestError,
  withExtClientId,
} from './surveys-api-v1.base';
import {
  SurveysApiAnswer,
  SurveysApiBreakoutPayload,
  SurveysApiCloneResult,
  SurveysApiCreateAnswerBody,
  SurveysApiCreateQuestionBody,
  SurveysApiCreateSurveyBody,
  SurveysApiListQuery,
  SurveysApiListResult,
  SurveysApiPublishResult,
  SurveysApiQuestion,
  SurveysApiResultsPayload,
  SurveysApiResultsQuery,
  SurveysApiSurveyDefinition,
  SurveysApiUpdateAnswerBody,
  SurveysApiUpdateQuestionBody,
  SurveysApiUpdateSurveyBody,
  SurveysApiVerbatimsPayload,
} from './surveys-api-v1.types';

const toQueryError = (error: unknown): UsableError => {
  const err = error as SurveysApiV1RequestError;
  return {
    code: err.status ?? 13,
    message: err.message || 'Surveys API request failed',
    httpStatus: err.status,
    responseData: err.code ? { code: err.code } : undefined,
  };
};

const base = () => getSurveysApiV1BaseUrl();

export const surveysApiV1Endpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getSurveysApiV1List: builder.query<SurveysApiListResult, SurveysApiListQuery | void>({
      async queryFn(arg) {
        try {
          const query = arg || {};
          const envelope = await surveysApiV1Envelope<
          import('./surveys-api-v1.types').SurveysApiSurveySummary[]
          >({
              method: 'GET',
              url: `${base()}/surveys`,
              params: withExtClientId(
                buildSurveysListQueryParams(query),
                query.extClientId
              ),
            });
          return { data: adaptSurveyListEnvelope(envelope) };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getSurveysApiV1Survey: builder.query<
    SurveysApiSurveyDefinition,
    { extSurveyId: string; extClientId?: string }
    >({
      async queryFn({ extSurveyId, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiSurveyDefinition>({
            method: 'GET',
            url: `${base()}/surveys/${extSurveyId}`,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    createSurveysApiV1Survey: builder.mutation<
    SurveysApiSurveyDefinition,
    { body: SurveysApiCreateSurveyBody; extClientId?: string; idempotencyKey?: string }
    >({
      async queryFn({ body, extClientId, idempotencyKey }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiSurveyDefinition>({
            method: 'POST',
            url: `${base()}/surveys`,
            data: body,
            params: withExtClientId(undefined, extClientId),
            headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    updateSurveysApiV1Survey: builder.mutation<
    SurveysApiSurveyDefinition,
    { extSurveyId: string; body: SurveysApiUpdateSurveyBody; extClientId?: string }
    >({
      async queryFn({ extSurveyId, body, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiSurveyDefinition>({
            method: 'PATCH',
            url: `${base()}/surveys/${extSurveyId}`,
            data: body,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    archiveSurveysApiV1Survey: builder.mutation<
    { ext_survey_id: string },
    { extSurveyId: string; extClientId?: string }
    >({
      async queryFn({ extSurveyId, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<{ ext_survey_id: string }>({
            method: 'DELETE',
            url: `${base()}/surveys/${extSurveyId}`,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    cloneSurveysApiV1Survey: builder.mutation<
    SurveysApiCloneResult,
    { extSurveyId: string; extClientId?: string }
    >({
      async queryFn({ extSurveyId, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiCloneResult>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/clone`,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    publishSurveysApiV1Survey: builder.mutation<
    SurveysApiPublishResult,
    { extSurveyId: string; maxCompletes?: number; extClientId?: string }
    >({
      async queryFn({ extSurveyId, maxCompletes, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiPublishResult>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/publish`,
            data: maxCompletes != null ? { max_completes: maxCompletes } : {},
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    createSurveysApiV1Question: builder.mutation<
    SurveysApiQuestion,
    { extSurveyId: string; body: SurveysApiCreateQuestionBody; extClientId?: string }
    >({
      async queryFn({ extSurveyId, body, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiQuestion>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/questions`,
            data: body,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    updateSurveysApiV1Question: builder.mutation<
    SurveysApiQuestion,
    {
      extSurveyId: string;
      questionId: string;
      body: SurveysApiUpdateQuestionBody;
      extClientId?: string;
    }
    >({
      async queryFn({ extSurveyId, questionId, body, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiQuestion>({
            method: 'PATCH',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}`,
            data: body,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    deleteSurveysApiV1Question: builder.mutation<
    { id: string },
    { extSurveyId: string; questionId: string; extClientId?: string }
    >({
      async queryFn({ extSurveyId, questionId, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<{ id: string }>({
            method: 'DELETE',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}`,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    reorderSurveysApiV1Questions: builder.mutation<
    { question_ids: string[] },
    { extSurveyId: string; questionIds: string[]; extClientId?: string }
    >({
      async queryFn({ extSurveyId, questionIds, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<{ question_ids: string[] }>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/questions/reorder`,
            data: questionIds,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    createSurveysApiV1Answer: builder.mutation<
    SurveysApiAnswer,
    {
      extSurveyId: string;
      questionId: string;
      body: SurveysApiCreateAnswerBody;
      extClientId?: string;
    }
    >({
      async queryFn({ extSurveyId, questionId, body, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiAnswer>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}/answers`,
            data: body,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    updateSurveysApiV1Answer: builder.mutation<
    SurveysApiAnswer,
    {
      extSurveyId: string;
      questionId: string;
      answerId: string;
      body: SurveysApiUpdateAnswerBody;
      extClientId?: string;
    }
    >({
      async queryFn({ extSurveyId, questionId, answerId, body, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiAnswer>({
            method: 'PATCH',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}/answers/${answerId}`,
            data: body,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    deleteSurveysApiV1Answer: builder.mutation<
    { id: string },
    { extSurveyId: string; questionId: string; answerId: string; extClientId?: string }
    >({
      async queryFn({ extSurveyId, questionId, answerId, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<{ id: string }>({
            method: 'DELETE',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}/answers/${answerId}`,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    reorderSurveysApiV1Answers: builder.mutation<
    { answer_ids: string[] },
    {
      extSurveyId: string;
      questionId: string;
      answerIds: string[];
      extClientId?: string;
    }
    >({
      async queryFn({ extSurveyId, questionId, answerIds, extClientId }) {
        try {
          const envelope = await surveysApiV1Envelope<{ answer_ids: string[] }>({
            method: 'POST',
            url: `${base()}/surveys/${extSurveyId}/questions/${questionId}/answers/reorder`,
            data: answerIds,
            params: withExtClientId(undefined, extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getSurveysApiV1Results: builder.query<
    SurveysApiResultsPayload,
    { extSurveyId: string } & SurveysApiResultsQuery
    >({
      async queryFn({ extSurveyId, extClientId, ...filters }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiResultsPayload>({
            method: 'GET',
            url: `${base()}/surveys/${extSurveyId}/results`,
            params: withExtClientId(buildResultsQueryParams(filters), extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getSurveysApiV1Breakout: builder.query<
    SurveysApiBreakoutPayload,
    { extSurveyId: string; breakout: string } & SurveysApiResultsQuery
    >({
      async queryFn({ extSurveyId, extClientId, breakout, ...filters }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiBreakoutPayload>({
            method: 'GET',
            url: `${base()}/surveys/${extSurveyId}/results/breakout`,
            params: withExtClientId(
              buildResultsQueryParams({ ...filters, breakout }),
              extClientId
            ),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),

    getSurveysApiV1Verbatims: builder.query<
    SurveysApiVerbatimsPayload,
    { extSurveyId: string } & SurveysApiResultsQuery
    >({
      async queryFn({ extSurveyId, extClientId, ...filters }) {
        try {
          const envelope = await surveysApiV1Envelope<SurveysApiVerbatimsPayload>({
            method: 'GET',
            url: `${base()}/surveys/${extSurveyId}/verbatims`,
            params: withExtClientId(buildResultsQueryParams(filters), extClientId),
          });
          return { data: envelope.data };
        } catch (error) {
          return { error: toQueryError(error) };
        }
      },
    }),
  }),
});

export const {
  useGetSurveysApiV1ListQuery,
  useLazyGetSurveysApiV1ListQuery,
  useGetSurveysApiV1SurveyQuery,
  useLazyGetSurveysApiV1SurveyQuery,
  useCreateSurveysApiV1SurveyMutation,
  useUpdateSurveysApiV1SurveyMutation,
  useArchiveSurveysApiV1SurveyMutation,
  useCloneSurveysApiV1SurveyMutation,
  usePublishSurveysApiV1SurveyMutation,
  useCreateSurveysApiV1QuestionMutation,
  useUpdateSurveysApiV1QuestionMutation,
  useDeleteSurveysApiV1QuestionMutation,
  useReorderSurveysApiV1QuestionsMutation,
  useCreateSurveysApiV1AnswerMutation,
  useUpdateSurveysApiV1AnswerMutation,
  useDeleteSurveysApiV1AnswerMutation,
  useReorderSurveysApiV1AnswersMutation,
  useGetSurveysApiV1ResultsQuery,
  useGetSurveysApiV1BreakoutQuery,
  useGetSurveysApiV1VerbatimsQuery,
} = surveysApiV1Endpoints;
